import React, { useMemo } from 'react';
import { isNull } from 'lodash/fp';

import { useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { Group, OfficeBuilding, User } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import styles from './Entity.module.scss';
import { EntityTypeSelector } from './EntityTypeSelector';
import { CompanyEntityForm } from './entityTypes/CompanyEntityForm';
import { GroupEntityForm } from './entityTypes/GroupEntityForm';
import { PersonEntityForm } from './entityTypes/PersonEntityForm';
import { CompanyEntity, EntityType, getAllEntities, getCurrentEntity, getEntityIdsForGroup, getIsSaving, getSelectedCompanySection, GroupEntity, PersonEntity } from './store';

interface EntityModalProps {
    isOpen: boolean;
    closeModal: () => void;
    uploadEntity: () => void;
}

export const EntityModal: React.FC<EntityModalProps> = ({ isOpen, closeModal, uploadEntity }) => {
    const entity = useAppSelector(getCurrentEntity);
    const entityIdsForGroup: string[] = useAppSelector(getEntityIdsForGroup);
    const selectedSection = useAppSelector(getSelectedCompanySection);
    const isSaving = useAppSelector(getIsSaving);
    const allEntities = useAppSelector(getAllEntities);
    const entityType = useMemo(() => entity && entity.type, [entity]);
    const isGroupEntityCheck = useMemo(() => !isNull(entity) && entity.type === EntityType.GROUP && entityIdsForGroup.length === 0, [entity, entityIdsForGroup]);

    const confirmLabel = useMemo(() => {
        if (isSaving) {
            return '';
        }
        return entity && entity.entityId ? 'Update' : 'Create';
    }, [isSaving, entity]);

    const relevantEntityNames = useMemo(() => allEntities.filter(({ type, entityId }) => type === entityType && entityId !== entity?.entityId).map(({ name }) => name), [allEntities, entityType, entity]);

    const nameIsNotUnique = useMemo(() => !isNull(entity) && relevantEntityNames.includes(entity.name), [entity, relevantEntityNames]);
    const modalTitle = useMemo(() => entity && entity.entityId ? 'Update Entity' : 'Create a New Entity', [entity]);
    const opinionSubCounterpartyTypeMissing = useMemo(() => (entity && entity.type === EntityType.COMPANY && entity.content.opinionSubCounterpartyTypes.some(({ value }) => isNull(value))) || false, [entity]);
    const confirmDisabled = useMemo(() => (entity && !entity.name) || isGroupEntityCheck || isSaving || nameIsNotUnique || opinionSubCounterpartyTypeMissing, [entity, isGroupEntityCheck, isSaving, nameIsNotUnique, opinionSubCounterpartyTypeMissing]);
    const confirmDisabledTooltip = useMemo(() => {
        if (entity && !entity.name) {
            return ['All entities require a name'];
        }
        if (isGroupEntityCheck) {
            return ['You must select at least one entity'];
        }
        if (nameIsNotUnique) {
            return ['Entity names must be unique'];
        }
        if (opinionSubCounterpartyTypeMissing) {
            return ['One or more of the selections for your opinion sub counterparty types is missing'];
        }
        return isSaving ? ['Saving your entity'] : [];
    }, [entity, isGroupEntityCheck, isSaving, nameIsNotUnique, opinionSubCounterpartyTypeMissing]);

    const createEntityFormByType = useMemo(() => {
        switch (entityType) {
            case EntityType.COMPANY:
                return <CompanyEntityForm entity={entity as CompanyEntity} selectedSection={selectedSection} />;
            case EntityType.PERSON:
                return <PersonEntityForm entity={entity as PersonEntity} />;
            case EntityType.GROUP:
                return <GroupEntityForm entity={entity as GroupEntity} />;
            default:
                return <EntityTypeSelector />;
        }
    }, [entityType, entity, selectedSection]);

    const headerIcon = useMemo(() => {
        switch (entityType) {
            case EntityType.COMPANY:
                return OfficeBuilding;
            case EntityType.GROUP:
                return Group;
            case EntityType.PERSON:
            default:
                return User;
        }
    }, [entityType]);

    useFetchStarted([fetchAllDropdownListsStarted()]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel={confirmLabel}
            confirmDisabled={confirmDisabled}
            confirm={uploadEntity}
            testId='entity'
            confirmDisabledTooltip={confirmDisabledTooltip}
            showConfirmSpinner={isSaving}
        >
            <div className={styles.entityModalWrapper} data-testid='entity-modal-wrapper'>
                <ModalHeader
                    label={modalTitle}
                    testId='entity-modal'
                    icon={headerIcon}
                    marginBottom='0px'
                />
                {createEntityFormByType}
            </div>
        </ConfirmationModal>
    );
};
